/**
 * `<FicheCoursWeb>` — LA FEUILLE, dans la console : une fiche de cours rendue
 * « telle que l'élève la lit » (chantier « manuel vivant », Φ-MV-2, MV-SAV-04).
 *
 * ─── Le même document, le même moteur, un second peintre ────────────────────
 *
 * L'application rend une `FicheCours` par `FicheCoursVue` (React Native). La
 * console n'a pas React Native : ce composant en est l'équivalent en HTML et
 * en SVG du DOM. Il ne réinterprète RIEN :
 *
 *  - les blocs sont regroupés par la même règle (`grouperBlocs`, recopiée de
 *    `FicheCoursVue` — pièges sous un même intitulé, « À retenir » et
 *    « Vérifie-toi » côte à côte) ;
 *  - les ancres sont celles d'`ancresDeFiche`, donc celles de la base ;
 *  - les formules posées sortent de `disposerMath`, les dessins de
 *    `resoudreDessin`, les traits du prof de `tracerAnnotation` — tous dans
 *    `@silex/shared` (`DECISIONS-ARCHITECTE.md`, point 13).
 *
 * Une fiche relue ici est donc la fiche servie : mêmes blocs, même ordre,
 * mêmes écritures.
 *
 * ─── Ce que la console montre EN PLUS ───────────────────────────────────────
 *
 * `pourRelecture` écrit sous chaque dessin la description qu'un lecteur
 * d'écran lira à l'élève. C'est le seul ajout, et il se voit comme tel.
 *
 * ─── L'ancre ────────────────────────────────────────────────────────────────
 *
 * `ancre="piege-mat-3-exp-n2-e-prod"` : la section visée défile dans la vue et
 * se met en évidence 2,6 s (la durée de l'application). Une ancre inconnue ne
 * fait rien. Le défilement est instantané si l'utilisateur a demandé moins
 * d'animations.
 *
 * Aucune couleur littérale : rôles et variables de `theme.css` (`.fiche-web`).
 */
import {
  INTITULES_FICHE,
  ancreDuPiege,
  ancresDeFiche,
  typographier,
  type BlocARetenir,
  type BlocFiche,
  type BlocIdee,
  type BlocPiege,
  type BlocPourquoi,
  type BlocTableauDeLois,
  type BlocVerifieToi,
  type FicheCours,
} from '@silex/shared';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { TexteMaths } from '../TexteMaths';

import { IllustrationWeb } from './DessinWeb';
import { ExempleAnnoteWeb } from './ExempleAnnoteWeb';
import { FormuleAjustee } from './FormuleWeb';
import { Icone, type NomIcone } from './icones';

/** Combien de temps l'endroit visé reste en évidence — la valeur de l'application. */
export const DUREE_EVIDENCE_MS = 2600;

/** L'identifiant DOM d'une ancre de fiche. Préfixé : il ne heurte aucun `id` de la page. */
export function idAncre(ancre: string): string {
  return `fiche-ancre-${ancre}`;
}

/** Les blocs, regroupés comme la feuille les présente. */
export type GroupeDeBlocs =
  | { readonly sorte: 'bloc'; readonly bloc: Exclude<BlocFiche, BlocPiege> }
  | { readonly sorte: 'pieges'; readonly pieges: readonly BlocPiege[] }
  | {
      readonly sorte: 'cloture';
      readonly blocs: readonly Extract<BlocFiche, { type: 'a_retenir' | 'verifie_toi' }>[];
    };

/**
 * Les pièges qui se suivent se rangent sous un même intitulé ; « À retenir »
 * suivi de « Vérifie-toi » se posent côte à côte, sous un filet. Même règle
 * que `grouperBlocs` de l'application.
 */
export function grouperBlocs(blocs: readonly BlocFiche[]): readonly GroupeDeBlocs[] {
  const groupes: GroupeDeBlocs[] = [];
  for (let i = 0; i < blocs.length; i += 1) {
    const bloc = blocs[i];
    if (bloc === undefined) continue;
    if (bloc.type === 'piege') {
      const dernier = groupes[groupes.length - 1];
      if (dernier?.sorte === 'pieges') {
        groupes[groupes.length - 1] = { sorte: 'pieges', pieges: [...dernier.pieges, bloc] };
      } else {
        groupes.push({ sorte: 'pieges', pieges: [bloc] });
      }
      continue;
    }
    if (bloc.type === 'a_retenir' || bloc.type === 'verifie_toi') {
      const suivant = blocs[i + 1];
      if (bloc.type === 'a_retenir' && suivant?.type === 'verifie_toi') {
        groupes.push({ sorte: 'cloture', blocs: [bloc, suivant] });
        i += 1;
      } else {
        groupes.push({ sorte: 'cloture', blocs: [bloc] });
      }
      continue;
    }
    groupes.push({ sorte: 'bloc', bloc });
  }
  return groupes;
}

/** Le texte courant d'une fiche : prose et MathML, typographie française posée. */
function Texte({ texte }: { readonly texte: string }): ReactNode {
  return <TexteMaths texte={typographier(texte)} />;
}

/* ─────────────────────────────────────────────────────────── la section */

interface Evidence {
  /** L'ancre mise en évidence en ce moment, ou `null`. */
  readonly ancre: string | null;
}

function classeAncre(base: string, ancre: string, evidence: Evidence): string {
  return evidence.ancre === ancre
    ? `${base} fiche-ancre fiche-ancre--visee`
    : `${base} fiche-ancre`;
}

function Section({
  ancre,
  intitule,
  icone,
  ton = 'accent',
  evidence,
  children,
}: {
  readonly ancre: string;
  readonly intitule: string;
  readonly icone: NomIcone;
  readonly ton?: 'accent' | 'ardoise';
  readonly evidence: Evidence;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <section
      id={idAncre(ancre)}
      className={classeAncre('fiche-section', ancre, evidence)}
      data-ancre={ancre}
    >
      <h3 className={`fiche-intitule fiche-intitule--${ton}`}>
        <Icone nom={icone} />
        <Texte texte={intitule} />
      </h3>
      {children}
    </section>
  );
}

/* ───────────────────────────────────────────────────── 1. l'idée en une phrase */

function BlocIdeeWeb({ bloc, evidence }: { bloc: BlocIdee; evidence: Evidence }): ReactNode {
  return (
    <Section ancre="idee" intitule={INTITULES_FICHE.idee} icone="ampoule" evidence={evidence}>
      <div className="fiche-idee">
        <p className="fiche-idee__phrase">
          <Texte texte={bloc.phrase} />
        </p>
        {bloc.appui !== undefined ? (
          <span className="fiche-idee__appui" data-testid="idee-appui">
            <FormuleAjustee tex={bloc.appui.formule} taille={22} />
          </span>
        ) : null}
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────────────────── 2. pourquoi c'est vrai */

function BlocPourquoiWeb({
  bloc,
  evidence,
  pourRelecture,
}: {
  bloc: BlocPourquoi;
  evidence: Evidence;
  pourRelecture: boolean;
}): ReactNode {
  return (
    <Section ancre="pourquoi" intitule={INTITULES_FICHE.pourquoi} icone="oeil" evidence={evidence}>
      {bloc.figures.map((figure, i) => (
        <div key={i} className="fiche-figure">
          <div className="fiche-figure__cadre">
            <IllustrationWeb illustration={figure.illustration} decritVisible={pourRelecture} />
          </div>
          {figure.legende !== undefined ? (
            <div className="fiche-figure__legende">
              {figure.legende.formule !== undefined ? (
                <FormuleAjustee tex={figure.legende.formule} taille={20} />
              ) : null}
              <p>
                <Texte texte={figure.legende.texte} />
              </p>
            </div>
          ) : null}
        </div>
      ))}
    </Section>
  );
}

/* ───────────────────────────────────────────────────────────── 4. un piège */

function CasePiege({
  genre,
  ecriture,
  raison,
}: {
  readonly genre: 'revoir' | 'juste';
  readonly ecriture: string;
  readonly raison: string;
}): ReactNode {
  const revoir = genre === 'revoir';
  return (
    <div
      className={`fiche-case fiche-case--${genre}`}
      data-testid={revoir ? 'piege-a-revoir' : 'piege-juste'}
    >
      {/* Le mot d'abord, pour qui écoute : « À revoir », puis l'écriture. La
          couleur seule ne dit rien — chaque case porte une icône ET un mot. */}
      <span className="fiche-case__etiquette">
        <Icone nom={revoir ? 'retour' : 'coche'} taille={14} epaisseur={2} />
        {revoir ? INTITULES_FICHE.aRevoir : INTITULES_FICHE.juste}
      </span>
      <span className="fiche-case__ecriture">
        <FormuleAjustee tex={ecriture} taille={20} ton={revoir ? 'ardoise' : 'encre'} />
      </span>
      <p className="fiche-case__raison">
        <Texte texte={raison} />
      </p>
    </div>
  );
}

function BlocPiegeWeb({
  bloc,
  evidence,
  pourRelecture,
}: {
  bloc: BlocPiege;
  evidence: Evidence;
  pourRelecture: boolean;
}): ReactNode {
  const ancre = ancreDuPiege(bloc.erreur);
  return (
    <article
      id={idAncre(ancre)}
      className={classeAncre('fiche-piege', ancre, evidence)}
      data-ancre={ancre}
    >
      <header className="fiche-piege__tete">
        <h4 className="fiche-piege__titre">
          <Texte texte={bloc.titre} />
        </h4>
        {/* La question à se poser : c'est elle qu'on emporte. */}
        <p className="fiche-piege__question">
          <Icone nom="question" epaisseur={2} />
          <span className="sr-only">{INTITULES_FICHE.demandeToi} </span>
          <Texte texte={bloc.question} />
        </p>
      </header>
      <div className="fiche-piege__grille">
        <CasePiege genre="revoir" ecriture={bloc.faux} raison={bloc.tentant} />
        <CasePiege genre="juste" ecriture={bloc.juste} raison={bloc.parce} />
        {bloc.figure !== undefined ? (
          <div className="fiche-piege__figure" data-testid="figure-du-piege">
            <IllustrationWeb
              illustration={bloc.figure}
              largeurMax={168}
              decritVisible={pourRelecture}
            />
          </div>
        ) : null}
      </div>
    </article>
  );
}

/* ───────────────────────────────────────────────────────────── 5. à retenir */

function BlocARetenirWeb({
  bloc,
  evidence,
}: {
  bloc: BlocARetenir;
  evidence: Evidence;
}): ReactNode {
  return (
    <Section
      ancre="a-retenir"
      intitule={bloc.intitule ?? INTITULES_FICHE.aRetenir}
      icone="signet"
      evidence={evidence}
    >
      <ul className="fiche-retenir">
        {bloc.lignes.map((ligne, i) => (
          <li key={i}>
            <Icone nom="coche" taille={18} epaisseur={2} />
            <span>
              <Texte texte={ligne} />
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/* ──────────────────────────────────────────────────────────── 6. vérifie-toi */

function BlocVerifieToiWeb({
  bloc,
  evidence,
  pourRelecture,
}: {
  bloc: BlocVerifieToi;
  evidence: Evidence;
  pourRelecture: boolean;
}): ReactNode {
  return (
    <Section
      ancre="verifie-toi"
      intitule={INTITULES_FICHE.verifieToi}
      icone="question"
      evidence={evidence}
    >
      <p>
        <Texte texte={bloc.question} />
      </p>
      {bloc.items !== undefined ? (
        <ul className="fiche-verif__items">
          {bloc.items.map((item, i) => (
            <li key={i}>
              <Texte texte={item} />
            </li>
          ))}
        </ul>
      ) : null}
      {/* La figure de la question (`CAP-06`) : AVANT la réponse — elle ne la donne pas. */}
      {bloc.figure !== undefined ? (
        <div className="fiche-verif__figure">
          <IllustrationWeb illustration={bloc.figure} decritVisible={pourRelecture} />
        </div>
      ) : null}
      {/* Ni saisie, ni correction, ni trace : ce n'est pas un exercice. Pour
          l'élève, la réponse est repliée ; le relecteur la lit d'emblée. */}
      <details className="fiche-verif__reponse" open={pourRelecture}>
        <summary>{INTITULES_FICHE.voirLaReponse}</summary>
        {bloc.reponse.map((paragraphe, i) => (
          <p key={i}>
            <Texte texte={paragraphe} />
          </p>
        ))}
      </details>
    </Section>
  );
}

/* ───────────────────────────────────────────────── 7. le tableau de lois */

function BlocTableauDeLoisWeb({
  bloc,
  evidence,
}: {
  bloc: BlocTableauDeLois;
  evidence: Evidence;
}): ReactNode {
  return (
    <Section ancre="lois" intitule={bloc.intitule} icone="liste" evidence={evidence}>
      <div className="fiche-lois" role="table" aria-label={bloc.intitule}>
        <div className="fiche-lois__tete" role="row">
          {bloc.colonnes.map((colonne, i) => (
            <span key={i} role="columnheader">
              <Texte texte={colonne} />
            </span>
          ))}
        </div>
        {bloc.lignes.map((ligne) => (
          <div
            key={ligne.ancre}
            id={idAncre(ligne.ancre)}
            className={classeAncre('fiche-lois__ligne', ligne.ancre, evidence)}
            data-ancre={ligne.ancre}
            role="row"
          >
            <div role="cell">
              <p className="fiche-lois__nom">
                <Texte texte={ligne.nom} />
                {ligne.renvoi !== undefined ? ` · fiche ${ligne.renvoi.rang}` : ''}
              </p>
              {ligne.regle.map((regle, k) => (
                <FormuleAjustee key={k} tex={regle} taille={18} />
              ))}
            </div>
            <div role="cell">
              <FormuleAjustee tex={ligne.exemple} taille={17} ton="accent" />
            </div>
            <div role="cell" className="fiche-lois__piege">
              <Icone nom="alerte" />
              <span>
                <Texte texte={ligne.piege} />
              </span>
            </div>
          </div>
        ))}
      </div>
      {bloc.conditions !== undefined ? (
        <p className="fiche-lois__conditions">
          <Texte texte={bloc.conditions} />
        </p>
      ) : null}
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────────── la feuille */

export interface FicheCoursWebProps {
  readonly fiche: FicheCours;
  /** L'ancre à viser. Change : la feuille y défile de nouveau. */
  readonly ancre?: string;
  /** Ce qui précède le titre, sans le redire : « Fiche 2 ». */
  readonly surtitre?: string;
  /** Écrit la description de chaque dessin, et déplie la réponse de « Vérifie-toi ». */
  readonly pourRelecture?: boolean;
}

export function FicheCoursWeb({
  fiche,
  ancre,
  surtitre,
  pourRelecture = false,
}: FicheCoursWebProps): ReactNode {
  const racine = useRef<HTMLElement>(null);
  /* L'ancre n'est retenue que si la fiche la porte : une ancre inconnue ouvre
     la fiche en haut, sans erreur. */
  const cible = useMemo(() => {
    if (ancre === undefined) return null;
    return ancresDeFiche(fiche).some((a) => a.ancre === ancre) ? ancre : null;
  }, [ancre, fiche]);
  const [visee, setVisee] = useState<string | null>(null);

  useEffect(() => {
    setVisee(cible);
    if (cible === null) return undefined;
    const el = racine.current?.querySelector<HTMLElement>(`#${CSS.escape(idAncre(cible))}`);
    const reduit = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    el?.scrollIntoView({ behavior: reduit ? 'auto' : 'smooth', block: 'start' });
    const fin = window.setTimeout(() => setVisee(null), DUREE_EVIDENCE_MS);
    return () => window.clearTimeout(fin);
  }, [cible]);

  const evidence = useMemo<Evidence>(() => ({ ancre: visee }), [visee]);
  const groupes = useMemo(() => grouperBlocs(fiche.blocs), [fiche.blocs]);

  return (
    <article ref={racine} className="fiche-web" data-testid={`fiche-${fiche.code}`}>
      <header className="fiche-entete">
        <p className="fiche-entete__sur">
          {surtitre !== undefined ? <span>{surtitre}</span> : null}
          <span className="fiche-entete__duree">
            <Icone nom="horloge" taille={14} />
            <span aria-hidden="true">{fiche.minutesLecture} min</span>
            <span className="sr-only">{fiche.minutesLecture} minutes de lecture</span>
          </span>
        </p>
        <h2 className="fiche-entete__titre">
          <Texte texte={fiche.titre} />
        </h2>
      </header>

      {groupes.map((groupe, i) => {
        if (groupe.sorte === 'pieges') {
          return (
            <Section
              key={i}
              ancre="pieges"
              intitule={groupe.pieges.length > 1 ? INTITULES_FICHE.pieges : INTITULES_FICHE.piege}
              icone="alerte"
              ton="ardoise"
              evidence={evidence}
            >
              {groupe.pieges.map((p) => (
                <BlocPiegeWeb
                  key={p.erreur}
                  bloc={p}
                  evidence={evidence}
                  pourRelecture={pourRelecture}
                />
              ))}
            </Section>
          );
        }
        if (groupe.sorte === 'cloture') {
          return (
            <div key={i} className="fiche-cloture">
              {groupe.blocs.map((b) =>
                b.type === 'a_retenir' ? (
                  <BlocARetenirWeb key={b.type} bloc={b} evidence={evidence} />
                ) : (
                  <BlocVerifieToiWeb
                    key={b.type}
                    bloc={b}
                    evidence={evidence}
                    pourRelecture={pourRelecture}
                  />
                ),
              )}
            </div>
          );
        }
        const { bloc } = groupe;
        switch (bloc.type) {
          case 'idee':
            return <BlocIdeeWeb key={i} bloc={bloc} evidence={evidence} />;
          case 'pourquoi':
            return (
              <BlocPourquoiWeb
                key={i}
                bloc={bloc}
                evidence={evidence}
                pourRelecture={pourRelecture}
              />
            );
          case 'exemple_annote':
            return (
              <Section
                key={i}
                ancre="exemple"
                intitule={INTITULES_FICHE.exemple}
                icone="plume"
                evidence={evidence}
              >
                <ExempleAnnoteWeb bloc={bloc} pourRelecture={pourRelecture} />
              </Section>
            );
          case 'tableau_de_lois':
            return <BlocTableauDeLoisWeb key={i} bloc={bloc} evidence={evidence} />;
          default:
            // `a_retenir` et `verifie_toi` sont toujours rangés en clôture.
            return null;
        }
      })}
    </article>
  );
}
